// Cross-tab mutual exclusion around token refresh.
//
// Every tab on this origin shares one refresh cookie, and the identity service rotates it on
// every use. Two tabs refreshing at the same instant both present the same cookie; the second
// replays a token the server has already revoked, gets a 401, and that 401 clears the cookie
// for everyone. Serialising refreshes across tabs removes the collision at its source: by the
// time the waiting tab runs, the cookie jar already holds the winner's fresh cookie and its
// refresh simply succeeds.
//
// Modelled on auth0-spa-js (src/lock.ts) and supabase auth-js (src/lib/locks.ts): an exclusive
// Web Lock, a bounded wait, and an unlocked fallback when the platform has no locks or the wait
// times out. A stuck lock must never be able to stop a refresh - supabase/supabase-js#1594
// deadlocked production with an unbounded wait.

export const REFRESH_LOCK_NAME = 'onecall-auth:refresh';
const ACQUIRE_TIMEOUT_MS = 5000;

export async function withRefreshLock(fn) {
  const locks = globalThis.navigator?.locks;
  if (!locks) return fn();

  // AbortController rather than AbortSignal.timeout(): the latter needs Safari 16.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ACQUIRE_TIMEOUT_MS);
  try {
    return await locks.request(
      REFRESH_LOCK_NAME,
      { mode: 'exclusive', signal: controller.signal },
      async () => {
        clearTimeout(timer);
        return fn();
      },
    );
  } catch (error) {
    if (error?.name !== 'AbortError') throw error;
    console.warn('[refreshLock] Lock not acquired within timeout; refreshing unlocked');
    return fn();
  } finally {
    clearTimeout(timer);
  }
}
